import { parse } from "chrono-node";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { DateTime } from "luxon";

import { formatUSPhoneNumber as formatSharedUSPhoneNumber } from "@/shared/utils/phone-utils";
import { logAndThrow, logger } from "@/src/lib/logging/logger";

export { formatIsoDate } from "@/shared/utils/date-utils";

/**
 * Date range filter options for filtering referrals and other date-based data
 */
export enum DateRangeFilter {
  LAST_24_HOURS = "last24hours",
  LAST_7_DAYS = "last7days",
  LAST_1_MONTH = "last1month",
}

export function normalizeSimpleDate(value: string | null | undefined): string | null {
  if (value == null) return null;

  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const slashMatch = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);

  if (slashMatch) {
    const [, m, d, y] = slashMatch;

    return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
  }

  return null;
}

export function sortByDateDescending<T>(items: T[], getDate: (item: T) => Date | null | undefined): T[] {
  return [...items].sort((a, b) => {
    const dateA = getDate(a)?.getTime() ?? 0;
    const dateB = getDate(b)?.getTime() ?? 0;

    return dateB - dateA;
  });
}

export function formatRelativeDate(dateString: string, baseMillis = Date.now()): string {
  const date = DateTime.fromISO(dateString);

  if (!date.isValid) {
    return dateString;
  }

  return (
    date.toRelative({
      base: DateTime.fromMillis(baseMillis),
      unit: ["days", "hours", "minutes", "seconds"],
      style: "short",
    }) || dateString
  );
}

/**
 * Formats an ISO date string in the timezone of the string (group timezone) as a concise date-time.
 * Renders in the runtime's local timezone by default (the browser zone for table cells). Pass an
 * explicit IANA `zone` when formatting server-side (e.g. CSV exports on Cloud Run, where "local"
 * is the container's TZ, not the viewer's).
 * @param isoString - The ISO date string (should carry group timezone from backend)
 * @param zone - Optional IANA zone to render in instead of the runtime's local zone
 * @param options.includeYear - Include the four-digit year when the surrounding context does not provide it
 * @returns A formatted concise date-time string (e.g., "Feb 19, 3:45 PM" or "Feb 19, 2026, 3:45 PM")
 */
export function formatDateTimeConcise(
  isoString: string,
  zone?: string,
  options: { includeYear?: boolean } = {}
): string {
  const dt = DateTime.fromISO(isoString, { setZone: true });

  if (!dt.isValid) {
    return isoString;
  }

  const zoned = zone ? dt.setZone(zone) : dt.toLocal();

  return zoned.toFormat(options.includeYear ? "MMM d, yyyy, h:mm a" : "MMM d, h:mm a");
}

export function formatDuration(startDateString: string, endDateString: string): string {
  const startDate = new Date(startDateString);
  const endDate = new Date(endDateString);
  const diffInMs = endDate.getTime() - startDate.getTime();
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInHours / 24);

  if (diffInDays > 0) {
    return `${diffInDays} day${diffInDays > 1 ? "s" : ""}`;
  } else if (diffInHours > 0) {
    return `${diffInHours} hour${diffInHours > 1 ? "s" : ""}`;
  } else {
    return `${diffInMinutes} minute${diffInMinutes !== 1 ? "s" : ""}`;
  }
}

/** Formats a duration in seconds as M:SS or H:MM:SS. */
export function formatDurationSeconds(seconds: number): string {
  const wholeSeconds = Number.isFinite(seconds) ? Math.floor(Math.max(0, seconds)) : 0;
  const hours = Math.floor(wholeSeconds / 3600);
  const minutes = Math.floor((wholeSeconds % 3600) / 60);
  const remainingSeconds = wholeSeconds % 60;
  const paddedSeconds = remainingSeconds.toString().padStart(2, "0");

  return hours > 0 ? `${hours}:${minutes.toString().padStart(2, "0")}:${paddedSeconds}` : `${minutes}:${paddedSeconds}`;
}

export function calendarDaysBetweenIsoDates(startIso: string, endIso: string, zone?: string): number {
  const start = DateTime.fromISO(startIso, { zone }).startOf("day");
  const end = DateTime.fromISO(endIso, { zone }).startOf("day");

  return Math.round(end.diff(start, "days").days);
}

export function formatHumanReadableDate(
  dateString: string,
  options?: {
    includeDate?: boolean;
    includeTime?: boolean;
    includeYear?: boolean;
    includeDayOfWeek?: boolean;
    includeTimezone?: boolean;
  }
): string {
  const date = DateTime.fromISO(dateString);

  if (!date.isValid) {
    return dateString;
  }

  if (options?.includeTimezone && !options?.includeTime) {
    throw new Error("includeTimezone requires includeTime to be true");
  }

  // Default to showing date if no options provided
  if (!options || Object.values(options).every((v) => !v)) {
    return date.toFormat("MMM d");
  }

  const formatParts: string[] = [];

  if (options.includeDayOfWeek) {
    formatParts.push("ccc");
  }

  if (options.includeDate) {
    formatParts.push("MMM d");
  }

  if (options.includeYear) {
    formatParts.push("yyyy");
  }

  if (options.includeTime) {
    formatParts.push("h:mm a");
  }

  if (options.includeTimezone) {
    formatParts.push("ZZZZ");
  }

  let formatString = formatParts.join(" ");

  // Add a comma between the date and year if both are included
  if (options.includeDate && options.includeYear) {
    formatString = formatString.replace("MMM d yyyy", "MMM d, yyyy");
  }

  // Add a comma between the day of the week and the date if both are included
  if (options.includeDayOfWeek && options.includeDate) {
    formatString = formatString.replace("ccc MMM d", "ccc, MMM d");
  }

  return date.toFormat(formatString);
}

// If a date is "2025-06-09" convert it to "06/09/2025"
export function convertDateStringToUSFormat(dateString: string): string {
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;

  if (!dateRegex.test(dateString)) {
    return dateString;
  }

  const [year, month, day] = dateString.split("-");

  return `${month}/${day}/${year}`;
}

export function getLocalDateMmDdYyyy(timezone: string, now: DateTime = DateTime.now()): string {
  return now.setZone(timezone).toFormat("MM/dd/yyyy");
}

// Formats a flexible date string as MM/dd/yyyy (e.g. for EHR date-input fields). Returns the raw
// string unchanged when it can't be parsed.
export function formatDateMmDdYyyy(dateString: string | null | undefined): string | null {
  if (!dateString) return null;

  const parsed = parseFlexibleDateStringToDateTime(dateString);

  return parsed?.isValid ? parsed.toFormat("MM/dd/yyyy") : dateString;
}

/** Formats a Date as `MM/dd/yyyy HH:mm:ss` in the given IANA timezone (e.g. Athena `showprocessedstartdatetime`). */
export function formatDateTimeMmDdYyyyHhMmSsInTimezone(date: Date, timezone: string): string {
  return DateTime.fromJSDate(date, { zone: timezone }).toFormat("MM/dd/yyyy HH:mm:ss");
}

/** The current instant in the given IANA timezone; falls back to the runtime zone when it is missing or unrecognized. */
export function getCurrentDateTimeInTimezone(timezone?: string | null, now: DateTime = DateTime.now()): DateTime {
  const normalizedTimezone = timezone?.trim();

  if (!normalizedTimezone) {
    return now;
  }

  const zoned = now.setZone(normalizedTimezone);

  return zoned.isValid ? zoned : now;
}

/** Today's calendar date (YYYY-MM-DD) in the given IANA timezone; falls back to the runtime zone. */
export function getCurrentIsoDateInTimezone(timezone?: string | null, now: DateTime = DateTime.now()): string {
  const zoned = getCurrentDateTimeInTimezone(timezone, now);

  return zoned.toISODate() ?? new Date().toISOString().slice(0, 10);
}

export function formatDate(dateString: string | null): string | null {
  if (!dateString) return null;

  const isoDateRegex = /^\d{4}-\d{2}-\d{2}$/;
  const usDateRegex = /^\d{1,2}\/\d{1,2}\/\d{4}$/; // Allow single or double digit for month and day

  if (isoDateRegex.test(dateString)) {
    return dateString; // Return as is if already in yyyy-mm-dd format
  } else if (usDateRegex.test(dateString)) {
    const [month, day, year] = dateString.split("/");
    const paddedMonth = month.padStart(2, "0");
    const paddedDay = day.padStart(2, "0");

    return `${year}-${paddedMonth}-${paddedDay}`; // Convert m/d/yyyy to yyyy-mm-dd
  } else {
    const date = new Date(dateString);

    return isNaN(date.getTime()) ? null : dateString; // Return null if not a valid date
  }
}

export function isUSFormatDate(dateString: string): boolean {
  const usDateRegex = /^\d{1,2}\/\d{1,2}\/\d{4}$/;

  return usDateRegex.test(dateString);
}

export { formatPhoneNumber, phoneNumberSchema } from "@/shared/utils/phone-utils";

export function formatUSPhoneNumber(phoneNumber: string | null, withValidation: boolean = false): string | null {
  if (!phoneNumber) return null;

  if (!withValidation) {
    return formatSharedUSPhoneNumber(phoneNumber);
  }

  try {
    const phoneNumberObj = parsePhoneNumberFromString(phoneNumber, "US");

    if (phoneNumberObj && phoneNumberObj.isValid()) {
      const nationalNumber = phoneNumberObj.nationalNumber;

      if (nationalNumber.length === 10) {
        const areaCode = nationalNumber.slice(0, 3);
        const centralOfficeCode = nationalNumber.slice(3, 6);
        const lineNumber = nationalNumber.slice(6);

        return `${areaCode}-${centralOfficeCode}-${lineNumber}`;
      }
    }
  } catch (error) {
    logger.error("Error parsing phone number", { error });
  }

  return null;
}

/**
 * Formats a phone number with a fallback if formatting fails
 * @param phoneNumber - The phone number to format
 * @param fallback - The fallback value if phoneNumber is null/undefined or formatting fails
 * @returns Formatted phone number or fallback value
 */
export function formatPhoneWithFallback(
  phoneNumber: string | null | undefined,
  fallback: string | null
): string | null {
  if (!phoneNumber) return fallback;

  const formatted = formatUSPhoneNumber(phoneNumber);

  return formatted || fallback;
}

/**
 * Formats a US phone/fax number as `(XXX)XXX-XXXX`. Returns "N/A" for null/undefined input
 * and falls back to the original string when it can't be reduced to 10 digits.
 */
export function formatFaxParens(phone: string | null | undefined): string {
  if (!phone) return "N/A";
  const digits = phone.replace(/\D/g, "");
  const ten = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;

  if (ten.length !== 10) return phone;

  return `(${ten.slice(0, 3)})${ten.slice(3, 6)}-${ten.slice(6)}`;
}

/**
 * Formats a phone number in real-time as the user types
 * Adds dashes automatically: 1234567890 -> 123-456-7890
 */
export function formatPhoneNumberAsTyped(value: string): string {
  // Remove all non-digit characters
  const digitsOnly = value.replace(/\D/g, "");

  // Limit to 10 digits
  const limitedDigits = digitsOnly.slice(0, 10);

  // Add dashes based on length
  if (limitedDigits.length <= 3) {
    return limitedDigits;
  } else if (limitedDigits.length <= 6) {
    return `${limitedDigits.slice(0, 3)}-${limitedDigits.slice(3)}`;
  } else {
    return `${limitedDigits.slice(0, 3)}-${limitedDigits.slice(3, 6)}-${limitedDigits.slice(6)}`;
  }
}

/**
 * Formats an SSN in real-time as the user types
 * Adds dashes automatically: 123456789 -> 123-45-6789
 */
export function formatSSNAsTyped(value: string): string {
  // Remove all non-digit characters
  const digitsOnly = value.replace(/\D/g, "");

  // Limit to 9 digits
  const limitedDigits = digitsOnly.slice(0, 9);

  // Add dashes based on length
  if (limitedDigits.length <= 3) {
    return limitedDigits;
  } else if (limitedDigits.length <= 5) {
    return `${limitedDigits.slice(0, 3)}-${limitedDigits.slice(3)}`;
  } else {
    return `${limitedDigits.slice(0, 3)}-${limitedDigits.slice(3, 5)}-${limitedDigits.slice(5)}`;
  }
}

/**
 * Calculates the correct cursor position after formatting (works for phone numbers, SSN, etc.)
 * @param oldValue - The previous value
 * @param newValue - The formatted value
 * @param oldCursorPosition - The cursor position before formatting
 * @returns The new cursor position
 */
export function calculateFormattedCursorPosition(
  oldValue: string,
  newValue: string,
  oldCursorPosition: number
): number {
  // Count digits before cursor in old value
  let digitsBeforeCursor = 0;

  for (let i = 0; i < Math.min(oldCursorPosition, oldValue.length); i++) {
    if (/\d/.test(oldValue[i])) {
      digitsBeforeCursor++;
    }
  }

  // Find position in new value where we've placed the same number of digits
  let newPosition = 0;
  let digitsPlaced = 0;

  for (let i = 0; i < newValue.length && digitsPlaced < digitsBeforeCursor; i++) {
    if (/\d/.test(newValue[i])) {
      digitsPlaced++;
    }

    newPosition = i + 1;
  }

  return newPosition;
}

/**
 * Formats a date as YYYY.MM.DD for use in filenames
 * Returns a placeholder if date is invalid or missing
 */
export function formatDateForFilename(dateString: string | null | undefined, separator: "" | "." = "."): string {
  if (!dateString) return `XXXX${separator}XX${separator}XX`;

  const isoDate = formatDate(dateString);

  if (!isoDate) return `XXXX${separator}XX${separator}XX`;

  return isoDate.substring(0, 10).replace(/-/g, separator);
}

const DEFAULT_RECENT_DAYS_THRESHOLD = 30;

/**
 * Checks if a date string represents an event within the given number of recent days.
 */
export function isWithinRecentDays(
  dateString: string | null | undefined,
  threshold = DEFAULT_RECENT_DAYS_THRESHOLD
): boolean {
  if (!dateString || dateString.trim() === "") {
    return false;
  }

  const formattedDate = formatDateForFilename(dateString);
  const eventDate = DateTime.fromFormat(formattedDate, "yyyy.MM.dd");

  if (!eventDate.isValid) {
    return false;
  }

  const days = DateTime.now().diff(eventDate, "days").days;

  return days >= 0 && days < threshold;
}

/**
 * Converts a date string from MM/DD/YYYY to YYYY-MM-DD format.
 * If the string is already in YYYY-MM-DD, it returns it as is.
 * Returns null for unrecognized formats.
 * @param dateStr - The date string to convert.
 * @returns The formatted date string or null.
 */
export function formatUsDateToYyyyMmDd(dateStr: string | null): string | null {
  if (!dateStr) return null;

  // Check if it's already in YYYY-MM-DD format
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return dateStr;
  }

  // Check for MM/DD/YYYY format and convert
  const mmDdYyyyRegex = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
  const match = dateStr.match(mmDdYyyyRegex);

  if (match) {
    const month = match[1].padStart(2, "0");
    const day = match[2].padStart(2, "0");
    const year = match[3];

    return `${year}-${month}-${day}`;
  }

  // Return null if the format is not recognized to avoid incorrect matches
  return null;
}

/**
 * Parses appointment date and time strings from Athena format to ISO format with Eastern Time zone
 *
 * @param date Date string in MM/DD/YYYY format
 * @param startTime Time string in HH:MM format
 * @returns ISO-formatted date-time string with Eastern Time zone, or null if date is not provided
 */
export function parseAppointmentDateTimeToEST(date: string | null, startTime: string | null): string | null {
  if (!date) return null;

  // Format date from MM/DD/YYYY to YYYY-MM-DD
  const [month, day, year] = date.split("/");
  const formattedDate = `${year}-${month}-${day}`;

  // Default time to midnight if not provided
  const formattedTime = startTime || "00:00:00";

  // Create Eastern Time zone identifier
  // This will handle both EST and EDT based on the date
  return `${formattedDate}T${formattedTime}-04:00`; // Eastern Time with UTC-4 offset (approximation)
}

export function parseAppointmentDateTimeWithDST(
  date: string | null,
  startTime: string | null,
  timezone: string = "America/New_York"
): string | null {
  if (!date) return null;

  const zone = timezone?.trim() || "America/New_York";
  const [month, day, year] = date.split("/");
  const [hours, minutes = 0] = (startTime || "00:00").split(":").map(Number);

  const dt = DateTime.fromObject(
    { year: Number(year), month: Number(month), day: Number(day), hour: hours, minute: minutes },
    { zone }
  );

  return dt.isValid ? dt.toISO() : null;
}

/**
 * Parses a natural language date/time phrase and returns an ISO date range.
 * @param phrase The natural language phrase (e.g., "next week", "tomorrow")
 * @param referenceDate The date to use as the 'now' reference (defaults to new Date())
 */
export function parseNaturalDateRange(
  phrase: string,
  referenceDate: Date = new Date()
): { start: string; end: string } | null {
  const parsed = parse(phrase, referenceDate);

  if (!parsed.length) return null;

  const start = parsed[0].start?.date();
  const end = parsed[0].end?.date() ?? start;

  if (!start) return null;

  return {
    start: start.toISOString().slice(0, 10),
    end: end.toISOString().slice(0, 10),
  };
}

/**
 * Function to convert a date to the FHIR input format for FHIR APIs.
 *
 * FHIR APIs use a date format of <ge | lt | gt | eq >YYYY-MM-DD
 *
 * ge2025-07-24 is the same as >=2025-07-24
 * lt2025-07-24 is the same as <2025-07-24
 * gt2025-07-24 is the same as >2025-07-24
 * eq2025-07-24 is the same as =2025-07-24
 *
 * @param date - Date to convert
 * @param operator - Operator to use in the FHIR input format. Must take values of (ge, lt, gt, eq)
 * @returns date in YYYY-MM-DD format with the operator prepended
 */
export function convertDateToFhirFormat(date: Date, operator: "ge" | "lt" | "gt" | "eq"): string {
  // Convert date to YYYY-MM-DD format first
  const year = date.getFullYear();

  // Pad month and day with 0 if they are single digits
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  // Return in geYYYY-MM-DD format

  if (!year || !month || !day) {
    logAndThrow("Invalid date", {
      date: date,
      operator: operator,
    });
  }

  return `${operator}${year}-${month}-${day}`;
}

export function formatHumanReadableDateNoYear(dateString: string): string {
  const date = new Date(dateString);

  return date.toLocaleString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Parses an ISO 8601 datetime string and formats it into a human-readable
 * string in the user's local timezone, excluding the year.
 *
 * @param dateString - An ISO 8601 formatted datetime string (e.g., "2025-08-13T13:00:00+00:00").
 * @returns A formatted string like "Wed, Aug 13, 09:00 AM EDT".
 */
export function formatHumanReadableDateInLocalTimezone(dateString: string): string {
  const localDateTime = DateTime.fromISO(dateString).toLocal();

  // Using toFormat for consistency
  return localDateTime.toFormat("ccc, MMM d, hh:mm a ZZZZ");
}

/**
 * Formats a date key (YYYY-MM-DD) into a human readable string with the day of the week and month.
 * If the date is today or tomorrow, it will return "Today" or "Tomorrow" followed by the date.
 * @param dateKey - The date key to format
 * @returns A human readable string
 */
export function formatRelativeDateHeading(dateKey: string): string {
  const date = DateTime.fromISO(dateKey);

  const today = DateTime.local();
  const tomorrow = today.plus({ days: 1 });

  const formatted = date.toFormat("EEEE, MMMM d"); // e.g., "Friday, August 8"

  if (date.hasSame(today, "day")) return `Today (${formatted})`;

  if (date.hasSame(tomorrow, "day")) return `Tomorrow (${formatted})`;

  return formatted;
}

/**
 * Formats a date-time string into a human readable time string in the user's local timezone, showing the timezone.
 * @param isoString - The date-time string to format
 * @returns A human readable time string
 */
export function formatTime(isoString: string): string {
  return DateTime.fromISO(isoString).toLocal().toFormat("h:mm a ZZZZ");
}

/**
 * Formats an ISO date string in the timezone of the string (group timezone).
 * Does NOT convert to browser/local timezone.
 * @param isoString - The ISO date string (should carry group timezone from backend)
 * @returns A formatted time string (e.g. "7:15 AM")
 */
export function formatAbsoluteTime(isoString: string): string {
  const dt = DateTime.fromISO(isoString, { setZone: true });

  return dt.toFormat("h:mm a");
}

export function formatAbsoluteDateTime(isoString: string, zone?: string, includeTimezone: boolean = false): string {
  const dt = zone ? DateTime.fromISO(isoString).setZone(zone) : DateTime.fromISO(isoString, { setZone: true });

  if (!dt.isValid) return isoString;

  return dt.toFormat(includeTimezone ? "ccc, MMM d, h:mm a ZZZZ" : "ccc, MMM d, h:mm a");
}

export function formatDateTimeWithTimezoneForDisplay(
  isoString: string | null | undefined,
  timezone: string = "America/Los_Angeles",
  withTime: boolean = true
): string {
  if (!isoString) return "";

  const dt = DateTime.fromISO(isoString).setZone(timezone);

  if (!dt.isValid) return "";

  if (withTime) {
    return dt.toFormat("yyyy-MM-dd @ h:mm a");
  }

  return dt.toFormat("yyyy-MM-dd");
}

/**
 * Detects if a value is likely a phone number
 * Supports various formats: +1-555-123-4567, (555) 123-4567, 555.123.4567, etc.
 */
export function isPhoneNumber(value: string): boolean {
  // Remove all non-digit characters to check length and patterns
  const digitsOnly = value.replace(/\D/g, "");

  // Must have at least 10 digits (US format) and no more than 15 (international max)
  if (digitsOnly.length < 10 || digitsOnly.length > 15) {
    return false;
  }

  // Common phone number patterns
  const phonePatterns = [
    /^\+\d{1,4}[-.\s]?\(?\d{1,4}\)?[-.\s]?\d{1,4}[-.\s]?\d{1,9}$/, // International format
    /^\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/, // US format: (555) 123-4567
    /^\d{3}[-.\s]?\d{3}[-.\s]?\d{4}$/, // US format: 555-123-4567
    /^\d{10,15}$/, // Just digits: 5551234567
  ];

  return phonePatterns.some((pattern) => pattern.test(value.trim()));
}

/**
 * Whether a search query reads as a (possibly partial) phone number: only digits and common
 * phone punctuation, with at least 3 digits. Looser than {@link isPhoneNumber}, which requires a
 * complete 10–15 digit number — search boxes want fragment matches (an area code, "867-53", …).
 */
export function isPartialPhoneQuery(value: string): boolean {
  const digits = value.replace(/\D/g, "");

  return digits.length >= 3 && /^[\d\s()+.-]+$/.test(value.trim());
}

/**
 * Normalizes a phone or fax number to 10-digit NANP format (bare digits, no country code).
 *
 * Accepts E.164 (`+15558675309`), formatted national (`555-867-5309`), and other standard
 * formats as stored by the DB phone schema. Uses libphonenumber-js for robust parsing and
 * returns exactly 10 digits, or `null` if the number cannot be resolved to a valid NANP number.
 */
export function normalizePhoneToNanpDigits(phone: string | null | undefined): string | null {
  if (!phone) return null;

  try {
    const parsed = parsePhoneNumberFromString(phone, "US");

    if (parsed?.isValid() && parsed.countryCallingCode === "1" && parsed.nationalNumber.length === 10) {
      return parsed.nationalNumber;
    }
  } catch {
    // fall through to digit-based fallback
  }

  // Fallback: strip formatting and remove US country code if present
  const digits = phone.replace(/\D/g, "");
  const normalized = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;

  return normalized.length === 10 ? normalized : null;
}

/**
 * Formats a date value to YYYY.MM.DD format
 */
export function formatDateWithDots(date: Date): string {
  return date.toISOString().split("T")[0].replace(/-/g, ".");
}

/**
 * Mapping of date format specifications to Luxon format strings
 */
const DATE_FORMAT_SPEC_TO_LUXON: Record<string, string> = {
  "YYYYMMDD": "yyyyMMdd",
  "MMDDYYYY": "MMddyyyy",
  "MM/DD/YYYY": "MM/dd/yyyy",
  "MM-DD-YYYY": "MM-dd-yyyy",
  "DD/MM/YY": "dd/MM/yy",
  "YYYY/MM/DD": "yyyy/MM/dd",
  "YYYY-MM-DD": "yyyy-MM-dd",
};

/**
 * Converts format specification to Luxon format string
 */
export function convertToLuxonFormat(formatSpec: string): string {
  if (!(formatSpec in DATE_FORMAT_SPEC_TO_LUXON)) {
    throw new Error(`unrecognized formatSpec: ${formatSpec}`);
  }

  return DATE_FORMAT_SPEC_TO_LUXON[formatSpec];
}

export function parseFlexibleDateStringToDateTime(value: string): DateTime | null {
  const s = value.trim();

  if (!s) {
    return null;
  }

  const usIso = formatUsDateToYyyyMmDd(s);
  const compact8 = /^\d{8}$/.test(s);
  const parsedMs = Date.parse(s);

  return (
    [
      DateTime.fromISO(s, { setZone: true }),
      DateTime.fromSQL(s),
      ...(usIso ? [DateTime.fromISO(usIso)] : []),
      DateTime.fromFormat(s, "yyyy.MM.dd"),
      DateTime.fromFormat(s, "yyyy.M.d"),
      ...(compact8 ? [DateTime.fromFormat(s, "yyyyMMdd"), DateTime.fromFormat(s, "MMddyyyy")] : []),
      ...(!Number.isNaN(parsedMs) ? [DateTime.fromMillis(parsedMs)] : []),
    ].find((d) => d.isValid) ?? null
  );
}

/**
 * Formats a date value with a specified format
 */
export function formatDateWithSpec(value: unknown, formatSpec: string): string {
  let dateObj: DateTime | null = null;

  if (value instanceof Date) {
    dateObj = DateTime.fromJSDate(value);
  } else if (typeof value === "number" && Number.isFinite(value)) {
    dateObj = DateTime.fromMillis(value);
  } else if (typeof value === "string") {
    dateObj = parseFlexibleDateStringToDateTime(value);
  } else {
    return String(value);
  }

  return dateObj?.isValid ? dateObj.toFormat(convertToLuxonFormat(formatSpec)) : String(value);
}

export function hasExceededTime(originalTime: number, maxDurationMs: number, latestTime: number = Date.now()): boolean {
  return latestTime - originalTime >= maxDurationMs;
}

/**
 * Calculates age from a date of birth string (format: YYYY-MM-DD)
 * @param dateOfBirth - The date of birth string in YYYY-MM-DD format
 * @returns The age in years, or null if the date is invalid
 */
export function calculateAge(dateOfBirth: string | null): number | null {
  if (!dateOfBirth) {
    return null;
  }

  const birthDate = DateTime.fromISO(dateOfBirth);

  if (!birthDate.isValid) {
    return null;
  }

  const now = DateTime.now();
  const age = now.diff(birthDate, "years").years;

  return Math.floor(age);
}

/** Returns true if the patient's date of birth (YYYY-MM-DD) indicates they are under 18. */
export function isMinorDateOfBirth(dateOfBirth: string | null | undefined): boolean {
  const age = calculateAge(dateOfBirth ?? null);

  return age !== null && age < 18;
}
